import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, ShieldAlert, ShieldX, CheckCircle2, Circle, MinusCircle,
  AlertTriangle, Clock, Loader2, RefreshCw, ChevronRight, Scale,
} from "lucide-react";
import { getComplianceFile } from "../api/complianceApi";

// Compact compliance summary shown inline on the property campaign view (between
// Visibility and Campaign Representatives). It mirrors the full Compliance tab's
// status and listing checklist so agents can follow compliance without leaving
// the page; the full file (documents, price history, audit log, export) stays
// on the Compliance tab. Non-sale listings have no compliance file and the card
// hides itself (the fetch returns 404).
const STATUS_META = {
  compliant: { label: "Compliant", icon: ShieldCheck, classes: "bg-green-50 text-green-800 border-green-200" },
  action_required: { label: "Action Required", icon: ShieldAlert, classes: "bg-amber-50 text-amber-800 border-amber-200" },
  cannot_go_live: { label: "Cannot Go Live", icon: ShieldX, classes: "bg-red-50 text-red-800 border-red-200" },
};

const ITEM_STATUS = {
  current: { label: "Current", icon: CheckCircle2, classes: "text-green-700" },
  received: { label: "Received", icon: Clock, classes: "text-amber-700" },
  superseded: { label: "Superseded", icon: AlertTriangle, classes: "text-amber-700" },
  expired: { label: "Expired", icon: AlertTriangle, classes: "text-red-700" },
  missing: { label: "Not received", icon: Circle, classes: "text-red-600" },
  na: { label: "N/A", icon: MinusCircle, classes: "text-gray-400" },
};

export default function ComplianceChecklistCard({ propertyId, onOpenFull }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getComplianceFile(propertyId);
      setFile(res.data);
      setHidden(false);
    } catch (err) {
      // No compliance file for this listing (lease / not a sale): stay out of the way.
      if (err?.response?.status === 404) setHidden(true);
      else setError(err?.response?.data?.detail || "Could not load the compliance checklist.");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  const meta = STATUS_META[file?.listing_status] || STATUS_META.cannot_go_live;
  const StatusIcon = meta.icon;
  const listingItems = (file?.items || []).filter((i) => i.phase === "listing");

  let body = null;
  if (loading) {
    body = (
      <div className="flex items-center gap-2 py-2 text-[11px] text-[#374151] opacity-70">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading compliance status…
      </div>
    );
  } else if (error) {
    body = (
      <div className="flex items-center justify-between gap-3 py-2">
        <span className="text-[11px] text-red-600">{error}</span>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 text-[10px] font-bold text-[#55C5D0] hover:underline"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  } else if (file) {
    body = (
      <>
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 mb-3 ${meta.classes}`}>
          <StatusIcon className="w-4 h-4 shrink-0" />
          <span className="text-[12px] font-bold">{meta.label}</span>
        </div>
        {file.flags?.length > 0 && (
          <ul className="mb-3 space-y-1">
            {file.flags.slice(0, 6).map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-[#374151]">
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${f.severity === "block" ? "bg-red-500" : "bg-amber-500"}`} />
                <span>{f.message}</span>
              </li>
            ))}
          </ul>
        )}
        <ul className="space-y-1.5">
          {listingItems.map((item) => {
            const im = ITEM_STATUS[item.status] || ITEM_STATUS.missing;
            const ItemIcon = im.icon;
            return (
              <li key={item.requirement_key} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-[11px] text-[#374151]">
                  <ItemIcon className={`w-3.5 h-3.5 shrink-0 ${im.classes}`} />
                  {item.label}
                  {!item.is_required && item.status !== "na" && (
                    <span className="text-[9px] text-gray-400">(optional)</span>
                  )}
                </span>
                <span className={`text-[10px] font-semibold ${im.classes}`}>
                  {item.status === "na" ? "N/A" : im.label}
                </span>
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] flex items-center gap-2">
          <Scale className="w-3.5 h-3.5 text-[#55C5D0]" /> Compliance checklist
        </h3>
        {file && onOpenFull && (
          <button
            type="button"
            onClick={onOpenFull}
            className="inline-flex items-center gap-1 text-[10px] font-bold text-[#55C5D0] hover:underline whitespace-nowrap"
          >
            Open full file <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
      {body}
    </div>
  );
}
